import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { container } from "setup/dependency/inversify.config";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { RootState } from "setup";
import { IDirectorsService } from "services/DirectorsService";
import { IAddressesService } from "services/AddressesService";
import { DirectorModel } from "services/api/cplapi/CPLAPI";
import { DirectorLinkedClientPortalUserModel } from "services/api/cplauthapi/CPLAuthAPI";
import { DirectorUpdate, UpdateDirectorFailure, getFailedDirectorParts } from "../utils/directorUtils";
import { deleteDirector } from "./DirectorsThunks";

const initialState = {
  Director: undefined as DirectorModel | undefined,
  LinkedPortalUsers: [] as DirectorLinkedClientPortalUserModel[],
  IsLoading: false,
  IsSaving: false,
  IsDeleting: false,
  IsLoadingLinkedPortalUsers: false,
  IsUnlinkingPortalUser: false,
};

export const getDirector = createAsyncThunk("Directors/GetDirector", async (directorID: string) => {
  const directorsService = container.get<IDirectorsService>(SERVICE_TYPES.DirectorsService);
  return await directorsService.getDirector(directorID, true);
});

export const updateDirector = createAsyncThunk(
  "Directors/UpdateDirector",
  async (
    { directorID, director, address }: DirectorUpdate & { directorID: string },
    thunkAPI
  ): Promise<UpdateDirectorFailure[]> => {
    const directorsService = container.get<IDirectorsService>(SERVICE_TYPES.DirectorsService);
    const addressesService = container.get<IAddressesService>(SERVICE_TYPES.AddressesService);
    const [directorResult, addressResult] = await Promise.allSettled([
      director && directorsService.updateDirector(director),
      address && addressesService.updateAddress(address),
    ]);

    const failures = getFailedDirectorParts(director && directorResult, address && addressResult);
    if (failures.length === 0) thunkAPI.dispatch(getDirector(directorID));
    return failures;
  }
);

export const getDirectorLinkedPortalUsers = createAsyncThunk(
  "Directors/GetDirectorLinkedPortalUsers",
  async (directorID: string) => {
    const directorsService = container.get<IDirectorsService>(SERVICE_TYPES.DirectorsService);
    return await directorsService.getLinkedClientPortalUsers(directorID);
  }
);

export const unlinkDirectorPortalUser = createAsyncThunk(
  "Directors/UnlinkDirectorPortalUser",
  async ({ directorID, clientPortalUserID }: { directorID: string; clientPortalUserID: string }, thunkAPI) => {
    const directorsService = container.get<IDirectorsService>(SERVICE_TYPES.DirectorsService);
    await directorsService.unlinkClientPortalUser(directorID, clientPortalUserID);
    thunkAPI.dispatch(getDirectorLinkedPortalUsers(directorID));
  }
);

export const directorDetailsSlice = createSlice({
  name: "directorDetails",
  initialState,
  reducers: {
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDirector.pending, (state) => {
        state.IsLoading = true;
      })
      .addCase(getDirector.rejected, (state) => {
        state.IsLoading = false;
      })
      .addCase(getDirector.fulfilled, (state, action) => {
        state.IsLoading = false;
        state.Director = action.payload;
      })
      .addCase(updateDirector.pending, (state) => {
        state.IsSaving = true;
      })
      .addCase(updateDirector.rejected, (state) => {
        state.IsSaving = false;
        toast.error("Director failed to update");
      })
      .addCase(updateDirector.fulfilled, (state, action) => {
        state.IsSaving = false;
        const errors = action.payload;
        if (errors.length === 0) {
          toast.success("Director updated");
        } else {
          errors.forEach((error) => {
            toast.warning(`The ${error.part} couldn't be saved. Please check the form and try again.`);
          });
        }
      })
      .addCase(deleteDirector.pending, (state) => {
        state.IsDeleting = true;
      })
      .addCase(deleteDirector.rejected, (state) => {
        state.IsDeleting = false;
      })
      .addCase(deleteDirector.fulfilled, (state) => {
        state.IsDeleting = false;
      })
      .addCase(getDirectorLinkedPortalUsers.pending, (state) => {
        state.IsLoadingLinkedPortalUsers = true;
      })
      .addCase(getDirectorLinkedPortalUsers.rejected, (state) => {
        state.IsLoadingLinkedPortalUsers = false;
      })
      .addCase(getDirectorLinkedPortalUsers.fulfilled, (state, action) => {
        state.IsLoadingLinkedPortalUsers = false;
        state.LinkedPortalUsers = action.payload;
      })
      .addCase(unlinkDirectorPortalUser.pending, (state) => {
        state.IsUnlinkingPortalUser = true;
      })
      .addCase(unlinkDirectorPortalUser.rejected, (state) => {
        state.IsUnlinkingPortalUser = false;
      })
      .addCase(unlinkDirectorPortalUser.fulfilled, (state) => {
        state.IsUnlinkingPortalUser = false;
        toast.success("Portal account unlinked");
      });
  },
});

export const { resetState } = directorDetailsSlice.actions;

export const selectDirector = (state: RootState) => state.directorDetails.Director;
export const selectLinkedPortalUsers = (state: RootState) => state.directorDetails.LinkedPortalUsers;
export const selectIsLoading = (state: RootState) => state.directorDetails.IsLoading;
export const selectIsSaving = (state: RootState) => state.directorDetails.IsSaving;
export const selectIsDeleting = (state: RootState) => state.directorDetails.IsDeleting;
export const selectIsLoadingLinkedPortalUsers = (state: RootState) =>
  state.directorDetails.IsLoadingLinkedPortalUsers || state.directorDetails.IsUnlinkingPortalUser;

export default directorDetailsSlice.reducer;
